import React, { useState, useEffect } from 'react';
import { giftAPI } from '../../api';
import './GiftNotification.css';

function GiftNotification() {
    const [gifts, setGifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedGift, setSelectedGift] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [claimMessage, setClaimMessage] = useState(null);

    useEffect(() => {
        fetchAvailableGifts();
    }, []);

    const fetchAvailableGifts = async () => {
        try {
            if (!localStorage.getItem('authToken')) {
                setLoading(false);
                return;
            }

            const res = await giftAPI.getMyGifts();
            const data = Array.isArray(res.data) ? res.data : res.data.results || [];
            setGifts(data);
        } catch (err) {
            // Error fetching gifts
        } finally {
            setLoading(false);
        }
    };

    const openGiftModal = (gift) => {
        setSelectedGift(gift);
        setShowModal(true);
        setClaimMessage(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedGift(null);
        setClaimMessage(null);
    };

    const claimGift = async () => {
        if (!selectedGift) return;

        setClaiming(true);
        setClaimMessage(null);

        try {
            const res = await giftAPI.claimGift(selectedGift.id);

            setClaimMessage({
                type: 'success',
                text: res.data.message || 'Gift claimed successfully!',
                rewards: res.data.rewards
            });

            // Refresh gift list after a delay
            setTimeout(() => {
                fetchAvailableGifts();
                closeModal();
            }, 3000);
        } catch (err) {
            setClaimMessage({
                type: 'error',
                text: err.response?.data?.error || 'Failed to claim gift. Please try again.'
            });
        } finally {
            setClaiming(false);
        }
    };

    const getRewardIcon = (rewardType) => {
        switch (rewardType) {
            case 'random_fragments': return '✨';
            case 'theme_fragments': return '🎨';
            case 'mutation_item': return '🧬';
            case 'random_mutation_items': return '🧬';
            case 'card_generation': return '👧';
            case 'global_wishes': return '💫';
            case 'random_card_from_list': return '👧';
            case 'booster': return '🎴';
            default: return '🎁';
        }
    };

    const renderRewardIcon = (reward) => {
        const image = reward.booster_type?.image_url;
        if (image) {
            return <img className="gift-reward-image" src={image} alt={reward.booster_type.name} />;
        }
        return getRewardIcon(reward.type);
    };

    if (loading || gifts.length === 0) {
        return null;
    }

    return (
        <>
            <div className="gift-notification-banner">
                <div className="gift-banner-content">
                    <div className="gift-banner-icon">🎁</div>
                    <div className="gift-banner-text">
                        <strong>You have {gifts.length} gift{gifts.length > 1 ? 's' : ''} waiting!</strong>
                        <p>Click to claim your rewards</p>
                    </div>
                </div>
                <div className="gift-banner-gifts">
                    {gifts.map(gift => (
                        <button
                            key={gift.id}
                            className="gift-item-button"
                            onClick={() => openGiftModal(gift)}
                        >
                            <span className="gift-item-icon">{getRewardIcon(gift.reward_type)}</span>
                            <span className="gift-item-title">{gift.title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {showModal && selectedGift && (
                <div className="gift-modal-overlay" onClick={closeModal}>
                    <div className="gift-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gift-modal-header">
                            <h2>🎁 {selectedGift.title}</h2>
                            <button className="gift-modal-close" onClick={closeModal}>×</button>
                        </div>

                        <div className="gift-modal-body">
                            <p className="gift-description">{selectedGift.description}</p>

                            {Array.isArray(selectedGift.reward_details) ? (
                                // Multiple rewards
                                <div>
                                    <h3 style={{ marginBottom: '15px' }}>Rewards:</h3>
                                    {selectedGift.reward_details.map((reward, idx) => (
                                        <div key={idx} className="gift-reward-display" style={{ marginBottom: '10px' }}>
                                            <div className="gift-reward-icon">
                                                {renderRewardIcon(reward)}
                                            </div>
                                            <div className="gift-reward-details">
                                                <h3>{reward.type_display}</h3>
                                                {reward.amount > 0 && reward.type !== 'card_generation' && reward.type !== 'random_card_from_list' && (
                                                    <p className="gift-reward-amount">
                                                        Amount: <strong>{reward.amount}</strong>
                                                    </p>
                                                )}
                                                {reward.theme && (
                                                    <p className="gift-reward-theme">
                                                        Theme: <strong>{reward.theme.name}</strong>
                                                    </p>
                                                )}
                                                {reward.booster_type && (
                                                    <p className="gift-reward-booster">
                                                        Booster: <strong>{reward.booster_type.name}</strong>
                                                    </p>
                                                )}
                                                {reward.characteristic && (
                                                    <p className="gift-reward-char">
                                                        {reward.characteristic.name}
                                                        ({reward.characteristic.category_display})
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Single reward (legacy)
                                <div className="gift-reward-display">
                                    <div className="gift-reward-icon">
                                        {renderRewardIcon(selectedGift.reward_details)}
                                    </div>
                                    <div className="gift-reward-details">
                                        <h3>{selectedGift.reward_details.type_display}</h3>
                                        {selectedGift.reward_details.amount > 0 && selectedGift.reward_details.type !== 'card_generation' && selectedGift.reward_details.type !== 'random_card_from_list' && (
                                            <p className="gift-reward-amount">
                                                Amount: <strong>{selectedGift.reward_details.amount}</strong>
                                            </p>
                                        )}
                                        {selectedGift.reward_details.theme && (
                                            <p className="gift-reward-theme">
                                                Theme: <strong>{selectedGift.reward_details.theme.name}</strong>
                                            </p>
                                        )}
                                        {selectedGift.reward_details.booster_type && (
                                            <p className="gift-reward-booster">
                                                Booster: <strong>{selectedGift.reward_details.booster_type.name}</strong>
                                            </p>
                                        )}
                                        {selectedGift.reward_details.characteristic && (
                                            <p className="gift-reward-char">
                                                {selectedGift.reward_details.characteristic.name}
                                                ({selectedGift.reward_details.characteristic.category_display})
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {claimMessage && (
                                <div className={`gift-claim-message ${claimMessage.type}`}>
                                    <p>{claimMessage.text}</p>
                                    {claimMessage.rewards && Array.isArray(claimMessage.rewards) && (
                                        <div className="reward-details">
                                            {claimMessage.rewards.map((reward, idx) => (
                                                <div key={idx} style={{ marginTop: '10px', padding: '10px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                                                    <p className="reward-message" style={{ fontWeight: 'bold', marginBottom: '8px' }}>{reward.message}</p>
                                                    {reward.mutation_items && Array.isArray(reward.mutation_items) && (
                                                        <div className="mutation-items-list" style={{ marginTop: '8px', paddingLeft: '15px' }}>
                                                            {reward.mutation_items.map((item, i) => (
                                                                <div key={i} style={{ marginBottom: '4px', fontSize: '14px' }}>
                                                                    🧬 {item}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {reward.card_name && (
                                                        <div className="card-generated" style={{ marginTop: '10px' }}>
                                                            <p><strong>🎉 New Card Generated!</strong></p>
                                                            <p>Name: <strong>{reward.card_name}</strong></p>
                                                            {reward.theme_name && (
                                                                <p>Theme: <strong>{reward.theme_name}</strong></p>
                                                            )}
                                                            <p style={{ marginTop: '10px', fontSize: '13px', opacity: 0.9 }}>
                                                                Check your collection to see your new card!
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="gift-modal-footer">
                            {claimMessage?.type !== 'success' && (
                                <button
                                    className="gift-claim-button"
                                    onClick={claimGift}
                                    disabled={claiming}
                                >
                                    {claiming ? 'Claiming...' : 'Claim Gift! 🎉'}
                                </button>
                            )}
                            <button className="gift-close-button" onClick={closeModal}>
                                {claimMessage?.type === 'success' ? 'Close' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default GiftNotification;
